import { apiGet, apiPost, apiPatch, apiClient } from '@/services/api/apiClient';
import { API_ENDPOINTS } from '@/constants/endpoints';

export type CourseLeadStatus =
  | 'new'
  | 'contacted'
  | 'in_progress'
  | 'qualified'
  | 'converted'
  | 'not_interested'
  | 'closed';

export interface CourseLeadComment {
  _id: string;
  text: string;
  addedBy?: { _id: string; name?: string };
  createdAt: string;
}

export interface CourseLeadCourseRef {
  _id: string;
  courseTitle?: string;
  level?: string;
  courseType?: string;
  price?: number;
  discount?: number;
}

export interface CourseLeadBusinessRef {
  _id: string;
  businessName?: string;
}

export interface CourseLeadUserRef {
  _id?: string;
  name?: string;
  email?: string;
  whatsappNumber?: string;
  userId?: string;
  city?: string;
  state?: string;
}

export interface CourseLead {
  _id: string;
  businessCourseId: string | CourseLeadCourseRef;
  businessId: string | CourseLeadBusinessRef;
  ownerId: string;
  userId: string | CourseLeadUserRef;
  name: string;
  mobileNumber: string;
  email: string;
  whatsappNumber?: string;
  location?: { city?: string; state?: string };
  profileLink?: string;
  courseTitle: string;
  status: CourseLeadStatus;
  comments: CourseLeadComment[];
  createdAt: string;
  updatedAt: string;
}

export interface MyCourseLeadsParams {
  page?: number;
  limit?: number;
  businessCourseId?: string;
  businessId?: string;
  status?: CourseLeadStatus;
  search?: string;
  startDate?: string;
  endDate?: string;
  days?: number;
}

export interface MyCourseLeadsResponse {
  success: boolean;
  message: string;
  data: {
    courseLeads: CourseLead[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}

export interface CourseLeadResponse {
  success?: boolean;
  message?: string;
  data: CourseLead;
}

export const courseLeadService = {
  async track(businessCourseId: string): Promise<CourseLeadResponse> {
    return apiPost<CourseLeadResponse>(API_ENDPOINTS.COURSE_LEADS.TRACK, { _id: businessCourseId });
  },

  async myLeads(params: MyCourseLeadsParams = {}): Promise<MyCourseLeadsResponse> {
    const sp = new URLSearchParams();
    if (params.page) sp.append('page', params.page.toString());
    if (params.limit) sp.append('limit', params.limit.toString());
    if (params.businessCourseId) sp.append('businessCourseId', params.businessCourseId);
    if (params.businessId) sp.append('businessId', params.businessId);
    if (params.status) sp.append('status', params.status);
    if (params.search) sp.append('search', params.search);
    if (params.startDate) sp.append('startDate', params.startDate);
    if (params.endDate) sp.append('endDate', params.endDate);
    if (params.days !== undefined) sp.append('days', params.days.toString());
    const query = sp.toString();
    return apiGet<MyCourseLeadsResponse>(
      `${API_ENDPOINTS.COURSE_LEADS.MY_LEADS}${query ? `?${query}` : ''}`
    );
  },

  async getById(id: string): Promise<CourseLeadResponse> {
    return apiGet<CourseLeadResponse>(API_ENDPOINTS.COURSE_LEADS.GET_BY_ID(id));
  },

  async updateStatus(id: string, status: CourseLeadStatus): Promise<CourseLeadResponse> {
    return apiPatch<CourseLeadResponse>(API_ENDPOINTS.COURSE_LEADS.UPDATE_STATUS(id), { status });
  },

  async addComment(id: string, text: string): Promise<CourseLeadResponse> {
    return apiPost<CourseLeadResponse>(API_ENDPOINTS.COURSE_LEADS.ADD_COMMENT(id), { text });
  },

  async exportExcel(
    params: { businessId?: string; businessCourseId?: string; status?: CourseLeadStatus; days?: number; startDate?: string; endDate?: string } = {}
  ): Promise<{ blob: Blob; filename: string }> {
    const sp = new URLSearchParams();
    if (params.businessId) sp.append('businessId', params.businessId);
    if (params.businessCourseId) sp.append('businessCourseId', params.businessCourseId);
    if (params.status) sp.append('status', params.status);
    if (params.days !== undefined) sp.append('days', params.days.toString());
    if (params.startDate) sp.append('startDate', params.startDate);
    if (params.endDate) sp.append('endDate', params.endDate);
    const query = sp.toString();
    const url = `${API_ENDPOINTS.COURSE_LEADS.EXPORT}${query ? `?${query}` : ''}`;
    const response = await (apiClient as any).client.get(url, { responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `course-leads-${new Date().toISOString().split('T')[0]}.xlsx`;
    return { blob: response.data as Blob, filename };
  },
};
